import type { CreateWork } from 'Work/components/Work/Operations/CreateWorkDialog'
import { Operation } from 'fast-json-patch'

import { ListAsset } from 'Common/components/assets/AssetGridList'
import { servicesAsync } from 'Common/environment/environment'
import {
    Activity,
    CommunicationPlan,
    LaunchPlan,
    WorkCost,
    WorkReviewQuote,
    WorkRoyalty,
} from 'Common/types'
import { fetchDelete, fetchGet, fetchPatch, fetchPost, fetchPut } from 'Common/utils/query/request'

const baseUrl = servicesAsync.work('/v1/works/')

interface WorkPatch {
    id: number
    patchSet: Operation[]
}

interface WorkTitlePatch {
    id: number
    patchSet: Operation[]
    editions: string | null
}

interface ReviewQuotesRequest {
    workId: number
    reviewQuotes: WorkReviewQuote[]
}

interface RoyaltyRequest {
    workId: number
    royalty: WorkRoyalty
}

interface WorkCostsRequest {
    workId: number
    workCosts: { items?: WorkCost[] }
}

interface SharedInfoRequest {
    workId: number
    editionId: number
    sharedInfo: any
}

interface WorkConceptRequest {
    workId: number
    body: any
}

interface WorkMainEditionRequest {
    workId: number
    mainEditionId: number
    externalPrimaryEditionId: number | null
}

interface WorkActivityPackageRequest {
    workId: number
    packageId: number
}

interface WorkAssetPostRequest {
    id: number
    asset: any
}

interface WorkAssetDeleteRequest {
    id: number
    assetId: number
}

interface WorkCopyRequest {
    workId: number
    data: {
        title: string
    }
}

interface WorkThemaPut {
    id: number
    themaId: string
    ids: string[]
}

export const getWork = async (id: number) => {
    return await fetchGet(`${await baseUrl}${id}`)
}

export const patchWork = async (workPatch: WorkPatch) => {
    return await fetchPatch(`${await baseUrl}${workPatch.id}`, JSON.stringify(workPatch.patchSet))
}

export const patchWorkTitle = async (workPatch: WorkTitlePatch) => {
    const url = workPatch.editions
        ? `${await baseUrl}${workPatch.id}?addOnProductions=${workPatch.editions}`
        : `${await baseUrl}${workPatch.id}`
    return await fetchPatch(url, JSON.stringify(workPatch.patchSet))
}

export const workPost = async (work: CreateWork) => {
    return await fetchPost(`${await baseUrl}`, JSON.stringify(work))
}

export const postWorkToERP = async (id: number) => {
    return await fetchPost(`${await baseUrl}${id}/connectToERP`, undefined)
}

export const postCancelWork = async (id: number) => {
    return await fetchPost(`${await baseUrl}${id}/cancel`, undefined)
}

export const getWorkCommunicationPlan = async (id: number) => {
    const result = await fetchGet(`${await baseUrl}${id}/communicationplan`)
    return result as CommunicationPlan
}

export const patchWorkCommunicationPlan = async (workPatch: WorkPatch) => {
    return await fetchPatch(
        `${await baseUrl}${workPatch.id}/communicationplan`,
        JSON.stringify(workPatch.patchSet),
    )
}

export const getWorkLaunchPlan = async (id: number) => {
    const result = await fetchGet(`${await baseUrl}${id}/launchplan`)
    return result as LaunchPlan
}

export const patchWorkLaunchPlan = async (workPatch: WorkPatch) => {
    return await fetchPatch(
        `${await baseUrl}${workPatch.id}/launchplan`,
        JSON.stringify(workPatch.patchSet),
    )
}

export const getLaunchPlanActivities = async (id: number) => {
    const result = await fetchGet(`${await baseUrl}${id}/activities`)
    return result.activities as Activity[]
}

export const postLaunchPlanActivityPackage = async (request: WorkActivityPackageRequest) => {
    return await fetchPost(
        `${await baseUrl}${request.workId}/addactivitytemplatepackage`,
        JSON.stringify({ id: request.packageId }),
    )
}

export const getWorkReviewQuotes = async (workId: number) => {
    return await fetchGet(`${await baseUrl}${workId}/reviewquotes`)
}

export const putWorkReviewQuotes = async (reviewQuotesRequest: ReviewQuotesRequest) => {
    return await fetchPut(
        `${await baseUrl}${reviewQuotesRequest.workId}/reviewquotes`,
        JSON.stringify({ reviewQuotes: reviewQuotesRequest.reviewQuotes }),
    )
}

export const getWorkRoyalty = async (workId: number) => {
    return await fetchGet(`${await baseUrl}${workId}/royalty`)
}

export const putWorkRoyalty = async (royaltyRequest: RoyaltyRequest) => {
    return await fetchPut(
        `${await baseUrl}${royaltyRequest.workId}/royalty`,
        JSON.stringify(royaltyRequest.royalty),
    )
}

export const putToSharedInfo = async (sharedInfoRequest: SharedInfoRequest) => {
    return await fetchPut(
        `${await baseUrl}${sharedInfoRequest.workId}/productions/${sharedInfoRequest.editionId}/sharedInfo`,
        JSON.stringify(sharedInfoRequest.sharedInfo),
    )
}

export const postWorkMainEdition = async (request: WorkMainEditionRequest) => {
    return await fetchPost(
        `${await baseUrl}${request.workId}/setmainedition`,
        JSON.stringify({
            externalPrimaryEditionId: request.externalPrimaryEditionId,
            mainEditionId: request.mainEditionId,
        }),
    )
}

export const postWorkConcept = async (request: WorkConceptRequest) => {
    return await fetchPost(
        `${await baseUrl}${request.workId}/createconcept`,
        JSON.stringify(request.body),
    )
}

export const getWorkThemas = async (workId: number) => {
    const result = await fetchGet(`${await baseUrl}${workId}/themas`)
    return result as any[]
}

export const workCostGet = async (workId: number) => {
    const result = await fetchGet(`${await baseUrl}${workId}/costs`)
    return result as any[]
}

export const putWorkCosts = async (workCostRequest: WorkCostsRequest) => {
    return await fetchPut(
        `${await baseUrl}${workCostRequest.workId}/costs`,
        JSON.stringify(workCostRequest.workCosts),
    )
}

export const workAssetsGet = async (workId: number) => {
    return (await fetchGet(`${await baseUrl}${workId}/assets`)) as ListAsset[]
}

export const workAssetsPost = async (request: WorkAssetPostRequest) => {
    return await fetchPost(`${await baseUrl}${request.id}/assets`, JSON.stringify(request.asset))
}

export const workAssetDelete = async (request: WorkAssetDeleteRequest) => {
    return await fetchDelete(`${await baseUrl}${request.id}/assets/${request.assetId}`)
}

export const workCopy = async (request: WorkCopyRequest) => {
    return await fetchPost(`${await baseUrl}${request.workId}/copy`, JSON.stringify(request.data))
}

export const workThemaPut = async (request: WorkThemaPut) => {
    return await fetchPut(
        `${await baseUrl}${request.id}/themas/${request.themaId}`,
        JSON.stringify(request.ids),
    )
}
